import { POSE_SKELETON_EDGES } from '../ml/moveNet';
import type { SamplePoseKeypoint } from '../data/types';

/**
 * Static pose-skeleton overlay drawn on top of a sample image. Used
 * by SampleThumb (compact) and SampleModal (full-size) so the kid
 * sees the same dots + connecting lines they saw in the viewfinder
 * persisted on every saved frame — same behaviour as Teachable
 * Machine.
 *
 * Implemented as an inline SVG with a 0..1 viewBox so the overlay
 * scales pixel-perfect to whatever <img> it's layered over without
 * needing a re-render on resize. The parent positions this absolute-
 * fill over the image and gives it `pointer-events: none`.
 */

const MIN_SCORE = 0.3;

export interface PoseOverlayProps {
  keypoints: ReadonlyArray<SamplePoseKeypoint>;
  /** Visual size of the dots. Larger by default for the modal where
   *  the image is bigger; the thumb passes a smaller value. Stroke
   *  width scales from this for a consistent look. */
  dotRadius?: number;
}

export function PoseOverlay({ keypoints, dotRadius = 0.025 }: PoseOverlayProps): JSX.Element {
  // 0..1 viewBox lets the SVG inherit the image's rendered size via
  // 100% width/height. Coordinates are already normalised, so we
  // pass them through directly.
  const stroke = dotRadius * 0.6;
  return (
    <svg
      className="bl-pose-overlay"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      aria-hidden="true"
      // Halo via SVG filter so the cyan stays legible over both
      // bright (white walls) and dark (carpet) sample backgrounds —
      // same treatment as the live capture overlay.
    >
      <g
        stroke="rgba(56, 189, 248, 0.95)"
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="rgba(56, 189, 248, 0.95)"
      >
        {POSE_SKELETON_EDGES.map(([a, b], i) => {
          const ka = keypoints[a];
          const kb = keypoints[b];
          if (!ka || !kb) return null;
          if (ka.score < MIN_SCORE || kb.score < MIN_SCORE) return null;
          return <line key={`e${i}`} x1={ka.x} y1={ka.y} x2={kb.x} y2={kb.y} />;
        })}
        {keypoints.map((kp, i) =>
          kp.score < MIN_SCORE ? null : (
            <circle key={`p${i}`} cx={kp.x} cy={kp.y} r={dotRadius} stroke="none" />
          ),
        )}
      </g>
    </svg>
  );
}
